import { IOauthToken, IUser } from 'src/models';
import { ILoginData } from 'src/screens';
import { IPasswordChangeRequest, IPasswordResetRequest } from 'src/screens/auth/types';

import { TApiResponseData } from '../apiTypes';
import instance from '../axios';

const AUTH_URL = '/v1/auth';
const USER_URL = '/v1/user';

/** 로그인 응답 데이터 타입 */
export interface ILoginResponse {
  accessToken: string;
  /** refresh token — localStorage에 저장하고 /v1/auth/refresh 요청 바디로 전송 */
  refreshToken?: string;
  tokenType: string;
  expiresIn: number;
  user: {
    userSid: number;
    loginId: string;
    userNm: string;
    acntSttsCdValue: number;
  };
}

/** 로그인 */
export const login = async (loginData: ILoginData) => {
  try {
    const url = `${AUTH_URL}/login`;

    // 스웨거 명세에 맞게 필드명 변환: loginId → id, pwd → password
    const payload = {
      id: loginData.loginId,
      password: loginData.pwd,
    };

    const response = await instance.post<TApiResponseData<ILoginResponse>>(url, payload);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** SNS 로그인 (surfinn 패턴) */
export const loginSns = async (data: any) => {
  try {
    // JWT 디코드는 필요시 추가
    // const json = jwtDecode(data.access_token.toString());
    const { access_token, refresh_token } = data;

    if (access_token) {
      return {
        success: true,
        message: '로그인 성공',
        access_token,
        refresh_token,
      };
    } else {
      return {
        success: false,
        message: data.errorMessage || 'SNS 로그인 실패',
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'SNS 로그인 실패',
    };
  }
};

/** 로그아웃 */
export const logout = async () => {
  try {
    const url = `${AUTH_URL}/logout`;

    const response = await instance.post<TApiResponseData<Record<string, never>>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 내 정보 조회 */
export const getUserProfile = async () => {
  try {
    const url = `${USER_URL}/me`;

    const response = await instance.get<TApiResponseData<IUser>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 아이디 중복 확인 */
export const checkDuplicateLoginId = async (loginId: string) => {
  try {
    const url = `${USER_URL}/exist/${loginId}`;

    const response = await instance.get<TApiResponseData<boolean>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 닉네임 중복 확인 */
export const checkDuplicateNickname = async (nickNm: string) => {
  try {
    const url = `${USER_URL}/exist/nick`;
    const response = await instance.get<TApiResponseData<Record<string, never>>>(url, {
      params: { nickNm },
    });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 아이디 찾기 (이메일 기반 — 본인인증 제거) */
export const findLoginId = async (email: string) => {
  try {
    const url = `${USER_URL}/find/loginId`;
    const payload = {
      email,
    };

    const response = await instance.post<TApiResponseData<string>>(url, payload);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 비밀번호 재설정 (로그인 이전) */
export const resetPwd = async (data: IPasswordResetRequest) => {
  try {
    const url = `${USER_URL}/reset/pwd`;

    const response = await instance.put<TApiResponseData<null>>(url, data);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 리프레시 토큰 응답 타입 */
export interface IRefreshResponse {
  accessToken: string;
  /** refreshTokenRenewed=true면 갱신된 refresh token이 함께 내려올 수 있음 */
  refreshToken?: string;
  tokenType: string;
  expiresIn: number;
  refreshTokenRenewed: boolean;
}

/** 토큰 재발급 — refresh token을 요청 바디로 전송 (localStorage 보관 방식) */
export const loginRefresh = async (refreshToken?: string | null) => {
  try {
    const url = `${AUTH_URL}/refresh`;

    // refresh token은 쿠키가 아니라 요청 바디로 전송한다.
    const response = await instance.post<TApiResponseData<IRefreshResponse>>(url, { refreshToken });
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 비밀번호 변경(로그인 이후) */
export const changePwd = async (data: IPasswordChangeRequest) => {
  try {
    const url = `${USER_URL}/change/pwd`;

    const response = await instance.put<TApiResponseData<null>>(url, data);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 회원가입 응답 타입 */
export interface ISignupResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: {
    userSid: number;
    loginId: string;
    userNm: string;
    acntSttsCdValue: number;
  };
}

/** 회원가입 요청 타입 (스웨거 명세 기준) */
export interface ISignupRequest {
  loginId?: string;
  pwd?: string;
  pwdConfirm?: string;
  userNm?: string;
  nickNm: string;
  phoneNo?: string;
  email?: string;
  snsKey?: string;
  /**
   * 약관 동의 내역 — BE 실제 형식은 객체 배열 [{ trmsSid, agreYn }].
   * (swagger yml에는 정수 배열로 잘못 정의돼 있으나 실제 구현은 객체 배열)
   * 모든 약관에 대해 agreYn(동의여부)을 명시한다. 필수=true, 선택=토글값.
   */
  termsAgreements?: Array<{
    trmsSid: number;
    agreYn: boolean;
  }>;
  trtmtPln?: {
    medSid?: number;
    medDoseSid?: number;
    /** 처방 시작일 — epoch timestamp(number) */
    prscStrtDay?: number;
    height?: number;
    initWght?: number;
    goalWght?: number;
    /** 투여 루틴 일시 — prscStrtDay+투여시각을 합친 epoch 밀리초 timestamp(number) */
    rtnTm?: number;
    monYn?: boolean;
    tueYn?: boolean;
    wedYn?: boolean;
    thuYn?: boolean;
    friYn?: boolean;
    satYn?: boolean;
    sunYn?: boolean;
  };
}

/** 회원가입 */
export const signup = async (formData: ISignupRequest) => {
  try {
    const url = `${USER_URL}/signup`;

    const response = await instance.post<TApiResponseData<ISignupResponse>>(url, formData);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 언어 변경 */
export const changeLanguage = async (langCd: string, refreshToken: string | null) => {
  try {
    const url = `${USER_URL}/lang`;
    const payload = {
      refreshToken,
      langCd,
    };

    const response = await instance.put<TApiResponseData<IOauthToken>>(url, payload);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};

/** 회원탈퇴 */
export const withdrawal = async () => {
  try {
    const url = `${USER_URL}/withdrawal`;

    const response = await instance.delete<TApiResponseData<null>>(url);
    return response.data;
  } catch (error) {
    // Error handling delegated to Axios interceptor
  }
};
